/**
 * @module reactivity
 *
 * The reactive core of Voodoo.js.
 *
 * The model: a Proxy plus per-key dependency tracking, scheduled on a
 * microtask. There is no Virtual DOM. When `count` changes, only the effects
 * that read `count` re-run, and each effect updates only the DOM node it wrote
 * itself.
 *
 * This module never touches the DOM and never assumes `window`, so it runs on
 * Node, Bun and Deno with no adaptation.
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type Dep = Set<ReactiveEffect>;
export type EffectScheduler = () => void;

export interface EffectOptions {
  /** Runs instead of the effect itself when a dependency changes. */
  scheduler?: EffectScheduler;
  /** Does not run immediately on creation. */
  lazy?: boolean;
  /** Called when the effect is stopped. */
  onStop?: () => void;
  /** The scope that owns the effect, for automatic cleanup. */
  scope?: EffectScope;
}

export interface Ref<T = any> {
  value: T;
  readonly __v_isRef: true;
}

export interface ComputedRef<T = any> extends Ref<T> {
  readonly value: T;
  readonly effect: ReactiveEffect;
}

export interface WatchOptions {
  immediate?: boolean;
  deep?: boolean;
  flush?: 'pre' | 'post' | 'sync';
}

export type WatchStopHandle = () => void;

// ---------------------------------------------------------------------------
// Scheduler
// ---------------------------------------------------------------------------

const resolvedPromise = /* @__PURE__ */ Promise.resolve();

let queue: ReactiveEffect[] = [];
let postQueue: Array<() => void> = [];
let isFlushing = false;
let isFlushPending = false;
let flushPromise: Promise<void> | null = null;

/** Maximum re-runs of the same effect within one flush, to catch loops. */
const RECURSION_LIMIT = 100;

/**
 * Resolves once the queue of updates has been applied to the DOM.
 *
 * ```js
 * count.value++
 * await V.nextTick()
 * // the DOM already reflects the change
 * ```
 */
export function nextTick<T = void>(fn?: () => T): Promise<T | void> {
  const p = flushPromise || resolvedPromise;
  return fn ? p.then(fn) : p;
}

export function queueJob(job: ReactiveEffect): void {
  // The flag on the effect itself replaces the earlier `queue.includes(job)`,
  // which swept the whole queue on every enqueue. With many distinct effects
  // invalidated in the same tick the cost was quadratic: a thousand effects cost
  // half a million comparisons just to discover that none was a duplicate.
  if (job.queued) return;
  job.queued = true;
  queue.push(job);
  queueFlush();
}

/** Schedules a callback to run after the DOM has been updated. */
export function queuePostFlush(cb: () => void): void {
  postQueue.push(cb);
  queueFlush();
}

function queueFlush(): void {
  if (isFlushing || isFlushPending) return;
  isFlushPending = true;
  flushPromise = resolvedPromise.then(flushJobs);
}

function flushJobs(): void {
  isFlushPending = false;
  isFlushing = true;
  const counts = new Map<ReactiveEffect, number>();

  try {
    for (let i = 0; i < queue.length; i++) {
      const job = queue[i];
      if (!job.active) continue;

      const count = (counts.get(job) || 0) + 1;
      counts.set(job, count);
      if (count > RECURSION_LIMIT) {
        warn(
          'Infinite update loop detected. A reactive effect keeps triggering ' +
            'itself without ever settling. Check whether some expression writes ' +
            'to state that it also reads.'
        );
        continue;
      }
      try {
        job.run();
      } catch (err) {
        handleError(err, 'effect');
      }
    }
  } finally {
    // Clear the flag on everything that was in the queue, including jobs that
    // joined during the flush itself.
    for (const job of queue) job.queued = false;
    queue = [];
    isFlushing = false;

    const posts = postQueue;
    postQueue = [];
    for (const cb of posts) {
      try {
        cb();
      } catch (err) {
        handleError(err, 'post-flush');
      }
    }

    // Effects queued during the post flush go into a fresh cycle.
    if (queue.length || postQueue.length) {
      flushPromise = resolvedPromise.then(flushJobs);
      isFlushPending = true;
    } else {
      flushPromise = null;
    }
  }
}

/** Applies everything still pending right away. Useful in tests. */
export function flushSync(): void {
  if (isFlushing) return;
  isFlushPending = false;
  flushJobs();
}

// ---------------------------------------------------------------------------
// Error handling
// ---------------------------------------------------------------------------

type ErrorHandler = (err: unknown, context: string) => void;

let errorHandler: ErrorHandler | null = null;

export function setErrorHandler(fn: ErrorHandler | null): void {
  errorHandler = fn;
}

export function handleError(err: unknown, context: string): void {
  if (errorHandler) {
    errorHandler(err, context);
    return;
  }
  // eslint-disable-next-line no-console
  console.error(`[Voodoo] error in ${context}:`, err);
}

export function warn(msg: string, ...args: unknown[]): void {
  // eslint-disable-next-line no-console
  console.warn(`[Voodoo] ${msg}`, ...args);
}

// ---------------------------------------------------------------------------
// Effects
// ---------------------------------------------------------------------------

let activeEffect: ReactiveEffect | undefined;
let shouldTrack = true;
const trackStack: boolean[] = [];

export function pauseTracking(): void {
  trackStack.push(shouldTrack);
  shouldTrack = false;
}

export function enableTracking(): void {
  trackStack.push(shouldTrack);
  shouldTrack = true;
}

export function resetTracking(): void {
  shouldTrack = trackStack.pop() ?? true;
}

export function getActiveEffect(): ReactiveEffect | undefined {
  return activeEffect;
}

let effectId = 0;

/**
 * Why every field here is `declare`d and then assigned in the constructor.
 *
 * The package compiles with `useDefineForClassFields`, and the build target is
 * below the level where class fields exist natively, so a field written as
 * `active = true` is emitted as an `Object.defineProperty` call. This class is
 * built once per binding on the page — one per row of a list, thousands of times
 * on a real screen — and the CPU profile of a thousand-row build showed the
 * define helper alone above one percent of total time. `declare` removes the
 * emitted definition, the constructor creates the same own, writable,
 * enumerable, configurable properties by plain assignment, and the assignment
 * order is kept identical so the object shape does not change.
 */
export class ReactiveEffect<T = any> {
  declare readonly id: number;
  declare active: boolean;
  /** `true` while the effect is waiting in the scheduler queue. */
  declare queued: boolean;
  declare deps: Dep[];
  declare parent: ReactiveEffect | undefined;
  declare scheduler: EffectScheduler | undefined;
  declare onStop: (() => void) | undefined;
  /** Cleanup callbacks registered by the effect itself. */
  declare cleanups: Array<() => void>;
  declare fn: () => T;

  constructor(fn: () => T, options?: EffectOptions) {
    this.fn = fn;
    this.id = effectId++;
    this.active = true;
    this.queued = false;
    this.deps = [];
    this.parent = undefined;
    this.scheduler = options?.scheduler;
    this.onStop = options?.onStop;
    this.cleanups = [];
    const scope = options?.scope ?? activeScope;
    if (scope) scope.effects.push(this);
  }

  run(): T | undefined {
    if (!this.active) return this.fn();

    // Prevents the same effect from recursing directly into itself.
    let parent: ReactiveEffect | undefined = activeEffect;
    while (parent) {
      if (parent === this) return undefined;
      parent = parent.parent;
    }

    this.runCleanups();

    try {
      this.parent = activeEffect;
      activeEffect = this;
      enableTracking();
      cleanupDeps(this);
      return this.fn();
    } finally {
      resetTracking();
      activeEffect = this.parent;
      this.parent = undefined;
    }
  }

  /** Registers a function called before the next run and on stop. */
  onInvalidate(fn: () => void): void {
    this.cleanups.push(fn);
  }

  private runCleanups(): void {
    if (!this.cleanups.length) return;
    const list = this.cleanups;
    this.cleanups = [];
    for (const fn of list) {
      try {
        fn();
      } catch (err) {
        handleError(err, 'effect cleanup');
      }
    }
  }

  stop(): void {
    if (!this.active) return;
    cleanupDeps(this);
    this.runCleanups();
    this.active = false;
    this.onStop?.();
  }
}

function cleanupDeps(effect: ReactiveEffect): void {
  const { deps } = effect;
  for (let i = 0; i < deps.length; i++) deps[i].delete(effect);
  deps.length = 0;
}

export interface EffectRunner<T = any> {
  (): T | undefined;
  effect: ReactiveEffect<T>;
}

/**
 * Creates a reactive effect. Runs once on creation and re-runs whenever any
 * state read inside it changes.
 *
 * ```js
 * const state = V.reactive({ count: 0 })
 * V.effect(() => console.log(state.count))
 * state.count++ // triggers the log
 * ```
 */
export function effect<T = any>(fn: () => T, options?: EffectOptions): EffectRunner<T> {
  const e = new ReactiveEffect(fn, options);
  if (!options?.lazy) e.run();
  const runner = (() => e.run()) as EffectRunner<T>;
  runner.effect = e;
  return runner;
}

export function stop(runner: EffectRunner | ReactiveEffect): void {
  if (runner instanceof ReactiveEffect) runner.stop();
  else runner.effect.stop();
}

// ---------------------------------------------------------------------------
// Effect scopes (batch cleanup)
// ---------------------------------------------------------------------------

let activeScope: EffectScope | undefined;

/** Fields are `declare`d and assigned in the constructor for the reason given on `ReactiveEffect`. */
export class EffectScope {
  declare effects: ReactiveEffect[];
  declare cleanups: Array<() => void>;
  declare children: EffectScope[];
  declare active: boolean;
  declare parent: EffectScope | undefined;

  constructor(detached = false) {
    this.effects = [];
    this.cleanups = [];
    this.children = [];
    this.active = true;
    this.parent = undefined;
    if (!detached && activeScope) {
      this.parent = activeScope;
      activeScope.children.push(this);
    }
  }

  run<T>(fn: () => T): T | undefined {
    if (!this.active) return undefined;
    const prev = activeScope;
    try {
      activeScope = this;
      return fn();
    } finally {
      activeScope = prev;
    }
  }

  onDispose(fn: () => void): void {
    this.cleanups.push(fn);
  }

  stop(): void {
    if (!this.active) return;
    this.active = false;
    for (const e of this.effects) e.stop();
    for (const c of this.children) c.stop();
    for (const fn of this.cleanups) {
      try {
        fn();
      } catch (err) {
        handleError(err, 'scope cleanup');
      }
    }
    this.effects.length = 0;
    this.children.length = 0;
    this.cleanups.length = 0;
    if (this.parent) {
      const i = this.parent.children.indexOf(this);
      if (i > -1) this.parent.children.splice(i, 1);
    }
  }
}

export function effectScope(detached = false): EffectScope {
  return new EffectScope(detached);
}

export function getActiveScope(): EffectScope | undefined {
  return activeScope;
}

// ---------------------------------------------------------------------------
// Tracking
// ---------------------------------------------------------------------------

export const ITERATE_KEY = Symbol('voodoo:iterate');

type TargetMap = WeakMap<object, Map<PropertyKey, Dep>>;
const targetMap: TargetMap = new WeakMap();

export function track(target: object, key: PropertyKey): void {
  if (!shouldTrack || !activeEffect) return;
  let depsMap = targetMap.get(target);
  if (!depsMap) targetMap.set(target, (depsMap = new Map()));
  let dep = depsMap.get(key);
  if (!dep) depsMap.set(key, (dep = new Set()));
  if (!dep.has(activeEffect)) {
    dep.add(activeEffect);
    activeEffect.deps.push(dep);
  }
}

export const enum TriggerType {
  SET = 'set',
  ADD = 'add',
  DELETE = 'delete',
  CLEAR = 'clear',
}

export function trigger(
  target: object,
  type: TriggerType,
  key?: PropertyKey,
  _newValue?: unknown
): void {
  const depsMap = targetMap.get(target);
  if (!depsMap) return;

  const effects = new Set<ReactiveEffect>();
  const add = (dep: Dep | undefined): void => {
    if (!dep) return;
    for (const e of dep) if (e !== activeEffect || type === TriggerType.CLEAR) effects.add(e);
  };

  if (type === TriggerType.CLEAR) {
    depsMap.forEach(add);
  } else {
    if (key !== undefined) add(depsMap.get(key));

    const isArr = Array.isArray(target);
    if (type === TriggerType.ADD) {
      if (!isArr) add(depsMap.get(ITERATE_KEY));
      else if (isIntegerKey(key)) add(depsMap.get('length'));
    } else if (type === TriggerType.DELETE) {
      add(depsMap.get(ITERATE_KEY));
    } else if (isArr && isIntegerKey(key)) {
      // Whoever walks the array depends on what is IN it, so `rows[3] = x` has
      // to reach an effect that iterated `rows` without ever naming index 3.
      // This used to be covered by accident: `v-for` read every element through
      // the proxy and so subscribed to all n indices, at the price of n
      // subscriptions per row per render. The list now subscribes once, to the
      // collection, and this line is what keeps element writes arriving.
      add(depsMap.get(ITERATE_KEY));
    } else if (isArr && key === 'length') {
      const newLen = Number(_newValue);
      depsMap.forEach((dep, k) => {
        if (k === 'length' || (typeof k !== 'symbol' && Number(k) >= newLen)) add(dep);
      });
    }
  }

  for (const e of effects) {
    if (e.scheduler) e.scheduler();
    else queueJob(e);
  }
}

/**
 * One aggregate notification for a mutation that touched a range of an array.
 *
 * The array mutators run against the raw array (see `arrayInstrumentations`),
 * so they no longer produce one trap — and one `trigger` — per element they
 * shuffle. `splice(5000, 1)` on ten thousand rows used to fire about five
 * thousand triggers, each allocating a Set and walking it, to describe a change
 * that is one removal.
 *
 * Everything that iterates the array hears about it through `length` and
 * `ITERATE_KEY`. An effect that read one specific index still has to hear about
 * a shift that moved a different element into it, so tracked integer keys at or
 * after `from` are notified too — only keys something actually subscribed to,
 * which for a list rendered by `v-for` is none.
 */
function triggerArrayRange(target: object, from: number): void {
  const depsMap = targetMap.get(target);
  if (!depsMap) return;

  const effects = new Set<ReactiveEffect>();
  const add = (dep: Dep | undefined): void => {
    if (!dep) return;
    for (const e of dep) if (e !== activeEffect) effects.add(e);
  };

  add(depsMap.get('length'));
  add(depsMap.get(ITERATE_KEY));
  if (from >= 0) {
    depsMap.forEach((dep, k) => {
      if (typeof k === 'string' && isIntegerKey(k) && Number(k) >= from) add(dep);
    });
  }

  for (const e of effects) {
    if (e.scheduler) e.scheduler();
    else queueJob(e);
  }
}

// ---------------------------------------------------------------------------
// Array mutation log
// ---------------------------------------------------------------------------

/**
 * What happened to an array, in the words of the call that did it.
 *
 * A reconciler handed a new array has to work out what changed by comparing it
 * against the old one, which costs one key read per row however clever it is.
 * A reconciler told "one row was removed at 5000" does not: the mutation
 * already carries the answer. This log is how the caller's knowledge survives
 * the trip to the effect that re-renders the list.
 */
export const enum ArrayOp {
  /** `removed` elements at `index` gave way to `added` new ones. */
  SPLICE = 1,
  /** The element at `index` was replaced, without shifting anything. */
  SET = 2,
  /** Something happened that no range can describe. History is unusable. */
  RESET = 3,
}

export interface ArrayMutation {
  type: ArrayOp;
  index: number;
  removed: number;
  added: number;
}

interface MutationLog {
  /** Mutations recorded since the array was first observed. Only ever grows. */
  version: number;
  /** The most recent mutations, oldest first. Bounded by `LOG_LIMIT`. */
  ops: ArrayMutation[];
}

/**
 * How much history to keep. A consumer that fell further behind than this gets
 * `null` and compares the lists instead, which is always correct and only
 * slower. Keeping more would help nobody: a burst of dozens of mutations
 * touches most of the list anyway, and comparing is then the cheaper answer.
 */
const LOG_LIMIT = 32;

const mutationLogs = new WeakMap<object, MutationLog>();

function record(target: object, type: ArrayOp, index: number, removed: number, added: number): void {
  let log = mutationLogs.get(target);
  if (!log) mutationLogs.set(target, (log = { version: 0, ops: [] }));
  log.version++;
  if (type === ArrayOp.RESET) {
    // Nothing recorded before this point can be replayed past it, and keeping
    // it would let a consumer believe a partial history was a whole one.
    log.ops.length = 0;
    return;
  }
  log.ops.push({ type, index, removed, added });
  if (log.ops.length > LOG_LIMIT) log.ops.shift();
}

const NO_MUTATIONS: ArrayMutation[] = [];

/** Mutations recorded for an array so far. `0` when it has never been mutated. */
export function arrayVersion(target: object): number {
  const log = mutationLogs.get(target);
  return log ? log.version : 0;
}

/**
 * What happened to `target` since version `since`, oldest first, or `null`
 * when the log cannot answer — the history was truncated, or something
 * unrepresentable happened. `null` means "compare the lists yourself".
 */
export function mutationsSince(target: object, since: number): ArrayMutation[] | null {
  const log = mutationLogs.get(target);
  if (!log) return since === 0 ? NO_MUTATIONS : null;
  if (since > log.version) return null;
  const missing = log.version - since;
  if (missing === 0) return NO_MUTATIONS;
  if (missing > log.ops.length) return null;
  return log.ops.slice(log.ops.length - missing);
}

function isIntegerKey(key: PropertyKey | undefined): boolean {
  return (
    typeof key === 'string' && key !== 'NaN' && key[0] !== '-' && String(parseInt(key, 10)) === key
  );
}

// ---------------------------------------------------------------------------
// reactive()
// ---------------------------------------------------------------------------

const RAW = Symbol('voodoo:raw');
const IS_REACTIVE = Symbol('voodoo:isReactive');
const SKIP = Symbol('voodoo:skip');

const reactiveMap = new WeakMap<object, any>();

const arrayInstrumentations: Record<string, Function> = /* @__PURE__ */ (() => {
  const inst: Record<string, Function> = {};
  for (const key of ['includes', 'indexOf', 'lastIndexOf'] as const) {
    inst[key] = function (this: unknown[], ...args: unknown[]) {
      const arr = toRaw(this) as any[];
      for (let i = 0; i < arr.length; i++) track(arr, String(i));
      const res = (arr[key] as Function).apply(arr, args);
      if (res === -1 || res === false) {
        return (arr[key] as Function).apply(arr, args.map(toRaw));
      }
      return res;
    };
  }
  // Mutators run against the RAW array and report the change once.
  //
  // They used to run against the proxy, which meant every element the method
  // shuffled passed through the `set` trap: `splice(5000, 1)` on ten thousand
  // rows produced five thousand trap calls and five thousand `trigger` calls to
  // describe one removal. Running on the raw array costs nothing per element,
  // and the single `triggerArrayRange` at the end says the same thing.
  //
  // The change is also what makes O(k) reconciliation possible at all: the
  // shape of the mutation is known here, exactly, and `record` is what carries
  // it to the list that has to redraw.
  for (const key of ['push', 'pop', 'shift', 'unshift', 'splice', 'reverse', 'sort'] as const) {
    inst[key] = function (this: unknown[], ...args: unknown[]) {
      const raw = toRaw(this) as any[];
      const before = raw.length;

      // The proxy's own write path called `toRaw` on every value it stored, so
      // storing raw here keeps what ends up in the array identical. `reactive()`
      // is applied again on the way out, so nothing downstream can tell.
      for (let i = 0; i < args.length; i++) args[i] = toRaw(args[i]);

      // A user comparator passed to `sort` must not subscribe whatever effect
      // is running to everything it happens to read.
      pauseTracking();
      let result: any;
      try {
        result = (raw[key] as Function).apply(raw, args);
      } finally {
        resetTracking();
      }

      const after = raw.length;
      let from = -1;

      if (key === 'push') {
        if (args.length) {
          record(raw, ArrayOp.SPLICE, before, 0, args.length);
          from = before;
        }
      } else if (key === 'unshift') {
        if (args.length) {
          record(raw, ArrayOp.SPLICE, 0, 0, args.length);
          from = 0;
        }
      } else if (key === 'pop') {
        if (before > 0) {
          record(raw, ArrayOp.SPLICE, after, 1, 0);
          from = after;
        }
      } else if (key === 'shift') {
        if (before > 0) {
          record(raw, ArrayOp.SPLICE, 0, 1, 0);
          from = 0;
        }
      } else if (key === 'splice') {
        const removed = (result as unknown[]).length;
        const added = args.length > 2 ? args.length - 2 : 0;
        if (removed || added) {
          record(raw, ArrayOp.SPLICE, spliceStart(args[0], before), removed, added);
          from = spliceStart(args[0], before);
        }
      } else {
        // reverse and sort: every position may have changed, and no range says
        // it. The list falls back to comparing, which is what it did before.
        if (before > 1) {
          record(raw, ArrayOp.RESET, 0, before, after);
          from = 0;
        }
      }

      if (from >= 0) triggerArrayRange(raw, from);

      // `pop`, `shift` and `splice` hand back elements. Read through the proxy
      // they came back reactive, and code that mutates a removed row expects
      // that to still work.
      if (key === 'pop' || key === 'shift') return isObject(result) ? reactive(result) : result;
      if (key === 'splice') {
        const out = result as unknown[];
        for (let i = 0; i < out.length; i++) if (isObject(out[i])) out[i] = reactive(out[i] as object);
        return out;
      }
      return key === 'reverse' || key === 'sort' ? this : result;
    };
  }
  return inst;
})();

function isObject(val: unknown): val is Record<PropertyKey, any> {
  return val !== null && typeof val === 'object';
}

/** `Array.prototype.splice`'s own reading of its first argument. */
function spliceStart(raw: unknown, length: number): number {
  const n = Math.trunc(Number(raw)) || 0;
  if (n < 0) return Math.max(length + n, 0);
  return Math.min(n, length);
}

const NON_REACTIVE = /* @__PURE__ */ new Set([
  'Date',
  'RegExp',
  'Promise',
  'Error',
  'File',
  'FileList',
  'Blob',
  'FormData',
  'URL',
  'URLSearchParams',
  'ArrayBuffer',
  'DataView',
]);

function canObserve(value: unknown): boolean {
  if (!isObject(value)) return false;
  if ((value as any)[SKIP]) return false;
  if (Object.isFrozen(value)) return false;
  if (typeof Node !== 'undefined' && value instanceof Node) return false;
  const tag = Object.prototype.toString.call(value).slice(8, -1);
  if (NON_REACTIVE.has(tag)) return false;
  return tag === 'Object' || tag === 'Array' || tag === 'Map' || tag === 'Set';
}

/**
 * Writes straight to an array — `rows[3] = x`, `rows.length = 0` — recorded in
 * the same terms the mutators use, so a list can act on them just as cheaply.
 */
function recordDirectWrite(
  target: any[],
  key: PropertyKey,
  lengthBefore: number,
  hadKey: boolean,
  oldValue: unknown,
  value: unknown
): void {
  if (key === 'length') {
    const next = target.length;
    // Shrinking drops a run off the end. Growing leaves holes, which no range
    // describes.
    if (next < lengthBefore) record(target, ArrayOp.SPLICE, next, lengthBefore - next, 0);
    else if (next > lengthBefore) record(target, ArrayOp.RESET, 0, 0, 0);
    return;
  }
  if (!isIntegerKey(key)) return;
  const index = Number(key);
  if (hadKey) {
    if (hasChanged(value, oldValue)) record(target, ArrayOp.SET, index, 1, 1);
  } else if (index === lengthBefore) {
    record(target, ArrayOp.SPLICE, index, 0, 1);
  } else {
    record(target, ArrayOp.RESET, 0, 0, 0);
  }
}

/** Marks an object so it is never turned into a reactive proxy. */
export function markRaw<T extends object>(value: T): T {
  Object.defineProperty(value, SKIP, { value: true, enumerable: false, configurable: true });
  return value;
}

/** Returns the original object behind a reactive proxy. */
export function toRaw<T>(observed: T): T {
  const raw = observed && (observed as any)[RAW];
  return raw ? toRaw(raw) : observed;
}

export function isReactive(value: unknown): boolean {
  return !!(value && (value as any)[IS_REACTIVE]);
}

/**
 * Makes an object deeply reactive.
 *
 * ```js
 * const state = V.reactive({ user: { name: 'Ana' }, tags: [] })
 * state.user.name = 'Bia'  // triggers effects that read user.name
 * state.tags.push('novo')  // triggers effects that read tags
 * ```
 */
export function reactive<T extends object>(target: T): T {
  if (!isObject(target)) return target;
  if (isReactive(target)) return target;
  if (!canObserve(target)) return target;

  const existing = reactiveMap.get(target);
  if (existing) return existing;

  // `WeakMap` and `WeakSet` are intentionally left out. They don't expose `size`,
  // iteration, or `forEach`, so there's no collection read to track: an effect
  // could only depend on a key it already has. Also, `canObserve()` won't let
  // these targets reach here, making the old `instanceof WeakMap` test dead code.
  // The general contract still holds: what can't be observed comes back as-is.
  const isMapOrSet = target instanceof Map || target instanceof Set;

  const proxy = new Proxy(
    target,
    isMapOrSet ? (collectionHandlers as ProxyHandler<T>) : (baseHandlers as ProxyHandler<T>)
  );
  reactiveMap.set(target, proxy);
  return proxy;
}

const baseHandlers: ProxyHandler<Record<PropertyKey, any>> = {
  get(target, key, receiver) {
    if (key === RAW) return target;
    if (key === IS_REACTIVE) return true;

    const isArr = Array.isArray(target);
    if (isArr && Object.prototype.hasOwnProperty.call(arrayInstrumentations, key)) {
      return Reflect.get(arrayInstrumentations, key, receiver);
    }

    const res = Reflect.get(target, key, receiver);

    if (typeof key === 'symbol') return res;

    track(target, key);

    if (isRef(res)) return isArr && isIntegerKey(key) ? res : res.value;
    if (isObject(res)) return reactive(res);
    return res;
  },

  set(target, key, value, receiver) {
    const oldValue = (target as any)[key];
    value = toRaw(value);

    if (!Array.isArray(target) && isRef(oldValue) && !isRef(value)) {
      oldValue.value = value;
      return true;
    }

    const hadKey = Array.isArray(target) && isIntegerKey(key)
      ? Number(key) < target.length
      : Object.prototype.hasOwnProperty.call(target, key);

    const isArr = Array.isArray(target);
    const arrayLength = isArr ? target.length : 0;

    const result = Reflect.set(target, key, value, receiver);
    if (target === toRaw(receiver)) {
      if (isArr) recordDirectWrite(target, key, arrayLength, hadKey, oldValue, value);
      if (!hadKey) trigger(target, TriggerType.ADD, key, value);
      else if (hasChanged(value, oldValue)) trigger(target, TriggerType.SET, key, value);
    }
    return result;
  },

  deleteProperty(target, key) {
    const hadKey = Object.prototype.hasOwnProperty.call(target, key);
    const result = Reflect.deleteProperty(target, key);
    if (result && hadKey) {
      // A hole is not a range edit, and pretending otherwise would let a list
      // skip rows that no longer exist.
      if (Array.isArray(target)) record(target, ArrayOp.RESET, 0, 0, 0);
      trigger(target, TriggerType.DELETE, key);
    }
    return result;
  },

  has(target, key) {
    const result = Reflect.has(target, key);
    if (typeof key !== 'symbol') track(target, key);
    return result;
  },

  ownKeys(target) {
    track(target, Array.isArray(target) ? 'length' : ITERATE_KEY);
    return Reflect.ownKeys(target);
  },
};

/** Handlers for Map and Set. */
const collectionHandlers: ProxyHandler<any> = {
  get(target, key, receiver) {
    if (key === RAW) return target;
    if (key === IS_REACTIVE) return true;

    const raw = target as Map<any, any> & Set<any>;

    if (key === 'size') {
      track(raw, ITERATE_KEY);
      return Reflect.get(raw, 'size', raw);
    }

    const methods: Record<string, Function> = {
      get(k: any) {
        track(raw, k);
        const v = raw.get(k);
        return isObject(v) ? reactive(v) : v;
      },
      has(k: any) {
        track(raw, k);
        return raw.has(k);
      },
      add(v: any) {
        v = toRaw(v);
        const had = raw.has(v);
        raw.add(v);
        if (!had) trigger(raw, TriggerType.ADD, v, v);
        return receiver;
      },
      set(k: any, v: any) {
        const had = raw.has(k);
        const old = raw.get(k);
        raw.set(k, toRaw(v));
        if (!had) trigger(raw, TriggerType.ADD, k, v);
        else if (hasChanged(v, old)) trigger(raw, TriggerType.SET, k, v);
        return receiver;
      },
      delete(k: any) {
        const had = raw.has(k);
        const res = raw.delete(k);
        if (had) trigger(raw, TriggerType.DELETE, k);
        return res;
      },
      clear() {
        const had = raw.size !== 0;
        const res = (raw as any).clear();
        if (had) trigger(raw, TriggerType.CLEAR);
        return res;
      },
      forEach(cb: Function, thisArg: any) {
        track(raw, ITERATE_KEY);
        return raw.forEach((v: any, k: any) => {
          cb.call(thisArg, isObject(v) ? reactive(v) : v, isObject(k) ? reactive(k) : k, receiver);
        });
      },
    };

    if (key in methods) return methods[key as string];

    if (key === Symbol.iterator || key === 'keys' || key === 'values' || key === 'entries') {
      track(raw, ITERATE_KEY);
      const method = (raw as any)[key];
      return typeof method === 'function' ? method.bind(raw) : method;
    }

    const res = Reflect.get(raw, key, raw);
    return typeof res === 'function' ? res.bind(raw) : res;
  },
};

export function hasChanged(value: unknown, oldValue: unknown): boolean {
  return !Object.is(value, oldValue);
}

// ---------------------------------------------------------------------------
// ref()
// ---------------------------------------------------------------------------

export function isRef<T>(r: unknown): r is Ref<T> {
  return !!(r && (r as any).__v_isRef === true);
}

class RefImpl<T> {
  readonly __v_isRef = true as const;
  private _value: T;
  private _rawValue: T;
  dep: Dep = new Set();

  constructor(value: T, private readonly shallow = false) {
    this._rawValue = toRaw(value);
    this._value = shallow ? value : (maybeReactive(value) as T);
  }

  get value(): T {
    trackRefValue(this);
    return this._value;
  }

  set value(newVal: T) {
    const raw = toRaw(newVal);
    if (hasChanged(raw, this._rawValue)) {
      this._rawValue = raw;
      this._value = this.shallow ? newVal : (maybeReactive(newVal) as T);
      triggerRefValue(this);
    }
  }
}

function maybeReactive(value: unknown): unknown {
  return isObject(value) ? reactive(value) : value;
}

function trackRefValue(ref: { dep: Dep }): void {
  if (!shouldTrack || !activeEffect) return;
  if (!ref.dep.has(activeEffect)) {
    ref.dep.add(activeEffect);
    activeEffect.deps.push(ref.dep);
  }
}

function triggerRefValue(ref: { dep: Dep }): void {
  for (const e of [...ref.dep]) {
    if (e.scheduler) e.scheduler();
    else queueJob(e);
  }
}

/**
 * Reactive reference for primitive values.
 *
 * ```js
 * const count = V.ref(0)
 * V.effect(() => console.log(count.value))
 * count.value++
 * ```
 */
export function ref<T>(value: T): Ref<T> {
  return new RefImpl(value) as unknown as Ref<T>;
}

export function shallowRef<T>(value: T): Ref<T> {
  return new RefImpl(value, true) as unknown as Ref<T>;
}

export function unref<T>(value: T | Ref<T>): T {
  return isRef(value) ? value.value : value;
}

// ---------------------------------------------------------------------------
// computed()
// ---------------------------------------------------------------------------

class ComputedRefImpl<T> {
  readonly __v_isRef = true as const;
  private _value!: T;
  private _dirty = true;
  readonly effect: ReactiveEffect<T>;
  dep: Dep = new Set();

  constructor(
    getter: () => T,
    private readonly setter?: (v: T) => void
  ) {
    this.effect = new ReactiveEffect(getter, {
      scheduler: () => {
        if (!this._dirty) {
          this._dirty = true;
          triggerRefValue(this);
        }
      },
    });
  }

  get value(): T {
    trackRefValue(this);
    if (this._dirty) {
      this._dirty = false;
      this._value = this.effect.run() as T;
    }
    return this._value;
  }

  set value(v: T) {
    if (this.setter) this.setter(v);
    else warn('computed is read-only when there is no setter.');
  }

  stop(): void {
    this.effect.stop();
  }
}

/**
 * Derived value with cache. Only recalculates when a dependency changes.
 *
 * ```js
 * const full = V.computed(() => `${state.first} ${state.last}`)
 * full.value
 * ```
 */
export function computed<T>(
  getterOrOptions: (() => T) | { get: () => T; set: (v: T) => void }
): ComputedRef<T> {
  const isFn = typeof getterOrOptions === 'function';
  const getter = isFn ? getterOrOptions : getterOrOptions.get;
  const setter = isFn ? undefined : getterOrOptions.set;
  return new ComputedRefImpl(getter, setter) as unknown as ComputedRef<T>;
}

// ---------------------------------------------------------------------------
// watch()
// ---------------------------------------------------------------------------

export type WatchSource<T = any> = Ref<T> | (() => T) | object;
export type WatchCallback<T = any> = (
  value: T,
  oldValue: T | undefined,
  onInvalidate: (fn: () => void) => void
) => void;

/**
 * Watches a reactive source and calls the callback when it changes.
 *
 * ```js
 * V.watch(() => state.search, (newValue, oldValue) => search(newValue))
 * ```
 */
export function watch<T>(
  source: WatchSource<T>,
  cb: WatchCallback<T>,
  options: WatchOptions = {}
): WatchStopHandle {
  const { immediate = false, deep = false, flush = 'pre' } = options;

  let getter: () => any;
  if (isRef(source)) getter = () => (source as Ref<T>).value;
  else if (typeof source === 'function') getter = source as () => T;
  else if (isReactive(source)) getter = () => traverse(source);
  else getter = () => source;

  if (deep) {
    const base = getter;
    getter = () => traverse(base());
  }

  let oldValue: any;
  let cleanupFn: (() => void) | undefined;
  const onInvalidate = (fn: () => void): void => {
    cleanupFn = fn;
  };

  const job = (): void => {
    if (!runner.effect.active) return;
    const newValue = runner();
    if (deep || hasChanged(newValue, oldValue)) {
      cleanupFn?.();
      cleanupFn = undefined;
      cb(newValue, oldValue, onInvalidate);
      oldValue = newValue;
    }
  };

  const scheduler =
    flush === 'sync' ? job : flush === 'post' ? () => queuePostFlush(job) : () => queueJob(runner.effect as any);

  // For 'pre' flush, the effect itself is rescheduled and the job runs within it.
  const runner =
    flush === 'pre'
      ? effect(getter, {
          lazy: true,
          scheduler: () => queuePostFlush(job),
        })
      : effect(getter, { lazy: true, scheduler });

  oldValue = runner();
  if (immediate) cb(oldValue, undefined, onInvalidate);

  return () => runner.effect.stop();
}

/** Executes the effect immediately and re-executes when dependencies change. */
export function watchEffect(fn: (onInvalidate: (c: () => void) => void) => void): WatchStopHandle {
  let cleanupFn: (() => void) | undefined;
  const onInvalidate = (c: () => void): void => {
    cleanupFn = c;
  };
  const runner = effect(() => {
    cleanupFn?.();
    cleanupFn = undefined;
    fn(onInvalidate);
  });
  return () => {
    cleanupFn?.();
    runner.effect.stop();
  };
}

function traverse(value: unknown, seen = new Set<unknown>()): unknown {
  if (!isObject(value) || seen.has(value)) return value;
  seen.add(value);
  if (isRef(value)) traverse((value as Ref).value, seen);
  else if (Array.isArray(value)) for (const v of value) traverse(v, seen);
  else if (value instanceof Set || value instanceof Map)
    (value as Set<unknown>).forEach((v) => traverse(v, seen));
  else for (const key of Object.keys(value)) traverse((value as any)[key], seen);
  return value;
}
